import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle, CheckCircle2, Eye, Clock, AlertTriangle, MousePointerClick,
  Filter, Globe, ExternalLink, RefreshCcw, MessageSquare, Reply, FileText,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

/**
 * Parent Dashboard · WhatsApp Outreach Log
 *
 * Super-admin surface that lists every WhatsApp message the platform has
 * dispatched, with live status + button-tap attribution. Complements the
 * Sales CRM's Outreach Analytics view — this is the raw feed.
 */

const STATUS_META = {
  queued: { label: "Queued", cls: "bg-secondary text-foreground", icon: Clock },
  sent: { label: "Sent", cls: "bg-blue-500/15 text-blue-500", icon: MessageCircle },
  delivered: { label: "Delivered", cls: "bg-emerald-500/15 text-emerald-600", icon: CheckCircle2 },
  read: { label: "Opened", cls: "bg-primary/20 text-primary", icon: Eye },
  failed: { label: "Failed", cls: "bg-destructive/15 text-destructive", icon: AlertTriangle },
  undelivered: { label: "Undelivered", cls: "bg-destructive/15 text-destructive", icon: AlertTriangle },
};

const BUTTON_LABELS = {
  jay7_trial: "JAY 7 Trial",
  coffee: "Coffee",
  training: "Training",
};

export default function WhatsAppLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [audienceFilter, setAudienceFilter] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: logs }, { data: an }] = await Promise.all([
        api.get("/whatsapp/logs", {
          params: {
            status: statusFilter || undefined,
            audience: audienceFilter || undefined,
            limit: 200,
          },
        }),
        api.get("/whatsapp/analytics"),
      ]);
      setItems(logs.items || []);
      setTotal(logs.total || 0);
      setAnalytics(an);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load WhatsApp log");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, audienceFilter, refreshTick]);

  const wa = analytics?.whatsapp || {};

  const kpis = useMemo(() => ([
    { key: "total", label: "Sent (total)", value: wa.total || 0, icon: MessageCircle, tint: "text-blue-500" },
    { key: "delivered", label: "Delivered", value: (wa.by_status?.delivered || 0) + (wa.by_status?.read || 0), icon: CheckCircle2, tint: "text-emerald-600" },
    { key: "opened", label: "Opened", value: wa.by_status?.read || 0, icon: Eye, tint: "text-primary" },
    { key: "clicked", label: "Clicked CTA", value: wa.clicked || 0, icon: MousePointerClick, tint: "text-amber-500" },
  ]), [wa]);

  return (
    <div className="space-y-8" data-testid="whatsapp-log-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-primary mb-2">Parent dashboard · WhatsApp</div>
          <h1 className="font-display text-4xl leading-tight">Interactive WhatsApp Outreach.</h1>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            Every WhatsApp message we've dispatched, with live delivery status and
            per-lead button-tap attribution. Sandbox recipients must first send
            <code className="mx-1 rounded bg-secondary px-1.5 py-0.5 text-xs">join &lt;keyword&gt;</code>
            to <b>+1 (415) 523-8886</b> to receive messages.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/admin/whatsapp/templates" data-testid="wa-log-templates-link">
            <Button variant="outline" className="rounded-full gap-1.5">
              <FileText className="h-3.5 w-3.5" /> Templates
            </Button>
          </Link>
          <Button
            variant="outline" onClick={() => setRefreshTick((t) => t + 1)}
            className="rounded-full gap-1.5" data-testid="wa-log-refresh"
          >
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </Button>
          <Link to="/app/admin"><Button variant="ghost" className="rounded-full">← Parent Dashboard</Button></Link>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.key} className="rounded-xl border border-border bg-card p-4" data-testid={`wa-kpi-${k.key}`}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              <k.icon className={"h-3.5 w-3.5 " + k.tint} />
              {k.label}
            </div>
            <div className="font-display text-3xl mt-2 tabular-nums">{k.value.toLocaleString()}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-3.5 w-3.5 text-muted-foreground" />
        <select
          value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-full border border-border bg-background px-3 text-sm capitalize"
          data-testid="wa-filter-status"
        >
          <option value="">All statuses</option>
          {Object.keys(STATUS_META).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={audienceFilter} onChange={(e) => setAudienceFilter(e.target.value)}
          className="h-9 rounded-full border border-border bg-background px-3 text-sm uppercase"
          data-testid="wa-filter-audience"
        >
          <option value="">All audiences</option>
          <option value="b2b">B2B</option>
          <option value="b2c">B2C</option>
        </select>
        <div className="ml-auto text-xs text-muted-foreground">
          Showing {items.length} of {total.toLocaleString()}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border overflow-hidden bg-card">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          <div className="col-span-3">Lead</div>
          <div className="col-span-2">Country</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Action taken</div>
          <div className="col-span-2">Sent</div>
          <div className="col-span-1 text-right">Open</div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground" data-testid="wa-log-empty">
            No WhatsApp messages yet. Send one from the Growth Engine to see it here.
          </div>
        ) : (
          items.map((m) => {
            const meta = STATUS_META[m.status] || STATUS_META.queued;
            const Icon = meta.icon;
            const action = m.action_taken;
            return (
              <div
                key={m.id}
                data-testid={`wa-row-${m.id}`}
                className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border last:border-0 items-center text-sm hover:bg-secondary/40 transition"
              >
                <div className="col-span-3 min-w-0">
                  <div className="font-medium truncate">{m.business_name || m.contact_name || "—"}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {m.to_phone} · <span className="uppercase">{m.audience || "b2b"}</span> · {m.pipeline}
                  </div>
                </div>
                <div className="col-span-2 flex items-center gap-1.5 text-muted-foreground">
                  <Globe className="h-3.5 w-3.5" />
                  <span className="truncate">{m.country || "—"}{m.city ? <span className="text-[11px]"> · {m.city}</span> : null}</span>
                </div>
                <div className="col-span-2">
                  <span className={"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] " + meta.cls}>
                    <Icon className="h-3 w-3" /> {meta.label}
                  </span>
                  {m.error_message && (
                    <div className="mt-1 text-[10px] text-destructive truncate" title={m.error_message}>
                      {m.error_message}
                    </div>
                  )}
                </div>
                <div className="col-span-2">
                  {action ? (
                    <span
                      data-testid={`wa-action-${m.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-600 px-2 py-0.5 text-[11px]"
                    >
                      <MousePointerClick className="h-3 w-3" />
                      {BUTTON_LABELS[action] || action}
                    </span>
                  ) : m.reply_intent ? (
                    <span
                      data-testid={`wa-reply-${m.id}`}
                      title={m.reply_body || ""}
                      className={
                        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] " +
                        (m.reply_intent === "positive"
                          ? "bg-emerald-500/15 text-emerald-600"
                          : m.reply_intent === "stop"
                          ? "bg-destructive/15 text-destructive"
                          : "bg-secondary text-foreground/70")
                      }
                    >
                      <Reply className="h-3 w-3" />
                      {m.reply_intent === "positive" ? "Positive"
                        : m.reply_intent === "stop" ? "Opted-out"
                        : "Replied"}
                    </span>
                  ) : <span className="text-muted-foreground text-[11px]">—</span>}
                </div>
                <div className="col-span-2 text-[11px] text-muted-foreground tabular-nums">
                  {m.sent_at ? new Date(m.sent_at).toLocaleString() : "—"}
                </div>
                <div className="col-span-1 flex justify-end">
                  {m.lead_id && (
                    <Link to={`/app/growth/leads/${m.lead_id}`}>
                      <button className="h-8 w-8 grid place-items-center rounded hover:bg-foreground/10" data-testid={`wa-open-lead-${m.id}`} title="Open lead">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </button>
                    </Link>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
